import { useRef, useCallback, useEffect } from 'react';
import type { Wall, FogReveal, Point, SerializedToken, GlobalIllumination, PageState, MapObject } from '@roll1/shared';
import { LIMITS } from '@roll1/shared';
import { computeVisibilityPolygon, isWallBlocking, createVisibilityCache } from '../lib/visibility';
import { VisibilityWorkerBridge } from '../lib/visibility-worker-bridge';
import { collectLightSources, computeLitZones } from '../lib/lighting';
import type { LitZone } from '../lib/lighting';
import { SpatialIndex } from '../lib/spatial-index';
import { computeExploredDiff } from '../lib/explored-mask';
import { computeEffectiveVision, isVisionDisabledByCondition } from '../lib/vision';
import { renderLightColorOverlay, collectColoredSources } from '../lib/light-renderer';
import { worldToScreen } from '../utils';
import type { CanvasEngine } from './useCanvasEngine';
import type { Camera } from '../types';

// --- Constants ---

/** Maximum debounce delay for explored updates (ms) */
const EXPLORED_DEBOUNCE_MS = 500;
/** Threshold for explored diff detection (world units) */
const EXPLORED_DIFF_THRESHOLD = 10;
/** Idle timeout before re-rendering at full resolution (ms) */
const IDLE_RERENDER_MS = 100;
/** Multi-resolution scale factor during camera movement */
const MOVING_SCALE = 0.5;
/** Viewport margin for culling (world-space pixels) — include walls slightly outside view */
const VIEWPORT_MARGIN = 200;

// --- Performance metrics (dev mode) ---

interface PerfMetrics {
  perTokenMs: number[];
  totalMs: number;
  cacheHits: number;
  cacheMisses: number;
  wallCount: number;
  tokenCount: number;
  frameCount: number;
}

// Expose perf metrics in dev mode
declare global {
  interface Window {
    __DYNAMIC_FOG_PERF?: PerfMetrics;
    __DYNAMIC_FOG_PERF_LOG?: boolean;
    __USE_VIS_WORKER?: boolean;
  }
}

export interface UseDynamicFogReturn {
  /** Render dynamic fog on the fog canvas. Call this instead of manual renderFog when mode=dynamic. */
  renderDynamicFog: () => void;
  /** Invalidate visibility cache (on token move / wall change) */
  invalidateVisibility: () => void;
  /** Invalidate explored cache (on new explored areas) */
  invalidateExplored: () => void;
  /** Invalidate all offscreen canvases (on page switch) */
  invalidateAll: () => void;
  /** Invalidate lit zone cache (on light/wall/globalIllumination change) */
  invalidateLighting: () => void;
  /** Sync explored areas from server state */
  setExplored: (explored: FogReveal[]) => void;
  /** Append new explored areas from server */
  appendExplored: (explored: FogReveal[]) => void;
  /** Get current explored areas */
  exploredRef: React.MutableRefObject<FogReveal[]>;
  /** Current visibility polygons (world-space) for token visibility filtering */
  visibilityPolygonsRef: React.MutableRefObject<Point[][]>;
  /** Cached lit zones for token visibility filtering in dark mode */
  litZoneCacheRef: React.MutableRefObject<LitZone[] | null>;
}

interface OffscreenCanvas {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  valid: boolean;
  width: number;
  height: number;
}

/**
 * Compute viewport AABB in world-space from camera and canvas dimensions.
 */
function computeViewportWorld(cam: Camera, cssW: number, cssH: number, margin: number) {
  // Screen (0,0) → world coordinates
  const worldMinX = -cam.offsetX / cam.zoom - margin;
  const worldMinY = -cam.offsetY / cam.zoom - margin;
  const worldMaxX = (cssW - cam.offsetX) / cam.zoom + margin;
  const worldMaxY = (cssH - cam.offsetY) / cam.zoom + margin;
  return { worldMinX, worldMinY, worldMaxX, worldMaxY };
}

export function useDynamicFog(
  engine: CanvasEngine,
  walls: React.MutableRefObject<Wall[]>,
  wallsVersion: React.MutableRefObject<number>,
  tokens: React.MutableRefObject<SerializedToken[]>,
  manualRevealed: React.MutableRefObject<FogReveal[]>,
  isGM: boolean,
  controlledTokenIds: React.MutableRefObject<Set<string>>,
  sendExploredUpdate?: (explored: FogReveal[]) => void,
  globalIllumination?: React.MutableRefObject<GlobalIllumination>,
  pageRef?: React.MutableRefObject<PageState | null>,
  gridSizeFt?: React.MutableRefObject<number>,
  mapObjectsRef?: React.MutableRefObject<MapObject[]>,
): UseDynamicFogReturn {
  // Offscreen canvases
  const exploredCanvasRef = useRef<OffscreenCanvas | null>(null);
  const visibilityCanvasRef = useRef<OffscreenCanvas | null>(null);
  const compositeCanvasRef = useRef<OffscreenCanvas | null>(null);
  const lightColorCanvasRef = useRef<OffscreenCanvas | null>(null);

  // Dirty flags
  const visibilityDirty = useRef(true);
  const exploredDirty = useRef(true);
  const compositeDirty = useRef(true);
  const lightingDirty = useRef(true);
  const lightColorDirty = useRef(true);

  // Explored state
  const exploredRef = useRef<FogReveal[]>([]);

  // Visibility cache (LRU)
  const visCacheRef = useRef(createVisibilityCache(50));

  // Spatial index for wall culling
  const spatialIndexRef = useRef(new SpatialIndex(64));
  const spatialIndexVersionRef = useRef(-1);

  // Previous wallsVersion for cache invalidation
  const prevWallsVersionRef = useRef(-1);

  // Last camera used for offscreen rendering (detect pan/zoom invalidation)
  const lastCamRef = useRef<{ offsetX: number; offsetY: number; zoom: number } | null>(null);

  // Last computed visibility polygons (for explored diff tracking)
  const lastVisibilityPolygonsRef = useRef<Point[][]>([]);

  // Debounce timer for explored updates
  const exploredDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingExploredRef = useRef<FogReveal[]>([]);
  // Guard: do not send explored updates until server state has been loaded.
  // Prevents spurious large-radius explores being sent during the first frames
  // when globalIlluminationRef is still the default 'bright' value.
  const exploredReadyRef = useRef(false);

  // Lit zone cache (world-space — does NOT invalidate on pan/zoom)
  const litZoneCacheRef = useRef<LitZone[] | null>(null);
  const litZoneWallsVersionRef = useRef(-1);

  // Multi-resolution: idle re-render timer
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMovingRef = useRef(false);

  // Performance metrics (dev mode only)
  const perfRef = useRef<PerfMetrics>({
    perTokenMs: [],
    totalMs: 0,
    cacheHits: 0,
    cacheMisses: 0,
    wallCount: 0,
    tokenCount: 0,
    frameCount: 0,
  });

  // Worker bridge for off-main-thread visibility pre-computation
  const workerBridgeRef = useRef<VisibilityWorkerBridge | null>(null);
  const precomputeScheduledRef = useRef(false);

  // Expose perf metrics in dev mode
  useEffect(() => {
    if (import.meta.env.DEV) {
      window.__DYNAMIC_FOG_PERF = perfRef.current;
    }
    workerBridgeRef.current = new VisibilityWorkerBridge();
    return () => {
      // Clean up idle timer on unmount
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
      workerBridgeRef.current?.dispose();
      workerBridgeRef.current = null;
    };
  }, []);

  // --- Offscreen canvas management ---

  function ensureOffscreen(ref: React.MutableRefObject<OffscreenCanvas | null>, w: number, h: number): OffscreenCanvas {
    let os = ref.current;
    if (!os) {
      const canvas = document.createElement('canvas');
      os = { canvas, ctx: canvas.getContext('2d')!, valid: false, width: 0, height: 0 };
      ref.current = os;
    }
    if (os.width !== w || os.height !== h) {
      os.canvas.width = Math.round(w);
      os.canvas.height = Math.round(h);
      os.width = w;
      os.height = h;
      os.valid = false;
    }
    return os;
  }

  // --- Invalidation ---

  const invalidateVisibility = useCallback(() => {
    visibilityDirty.current = true;
    compositeDirty.current = true;
    engine.markDirty('fog');
    engine.markDirty('token');
    scheduleWorkerPrecompute();
  }, [engine]);

  const invalidateExplored = useCallback(() => {
    exploredDirty.current = true;
    compositeDirty.current = true;
    engine.markDirty('fog');
  }, [engine]);

  const invalidateLighting = useCallback(() => {
    lightingDirty.current = true;
    lightColorDirty.current = true;
    litZoneCacheRef.current = null;
    compositeDirty.current = true;
    engine.markDirty('fog');
  }, [engine]);

  const invalidateAll = useCallback(() => {
    visibilityDirty.current = true;
    exploredDirty.current = true;
    compositeDirty.current = true;
    lightingDirty.current = true;
    lightColorDirty.current = true;
    litZoneCacheRef.current = null;
    visCacheRef.current.clear();
    prevWallsVersionRef.current = -1;
    spatialIndexVersionRef.current = -1;
    litZoneWallsVersionRef.current = -1;
    lastVisibilityPolygonsRef.current = [];
    lastCamRef.current = null;
    exploredReadyRef.current = false; // Reset on page switch — wait for setExplored again
    engine.markDirty('fog');
  }, [engine]);

  // --- Explored sync ---

  const setExplored = useCallback((explored: FogReveal[]) => {
    exploredRef.current = explored;
    exploredReadyRef.current = true; // Server state loaded — safe to send updates now
    exploredDirty.current = true;
    compositeDirty.current = true;
    engine.markDirty('fog');
  }, [engine]);

  const appendExplored = useCallback((explored: FogReveal[]) => {
    exploredRef.current.push(...explored);
    exploredDirty.current = true;
    compositeDirty.current = true;
    engine.markDirty('fog');
  }, [engine]);

  // --- Debounced explored update send ---

  function flushExploredUpdate() {
    if (pendingExploredRef.current.length > 0 && sendExploredUpdate) {
      sendExploredUpdate(pendingExploredRef.current);
      pendingExploredRef.current = [];
    }
  }

  function scheduleExploredUpdate(newExplored: FogReveal[]) {
    // Do not send until server-side explored state has been loaded (exploredReadyRef guard).
    // Prevents race-condition where first RAF frames run with globalIlluminationRef='bright'
    // (default) before useEffect sets it to the page's actual level, causing oversized explores.
    if (!exploredReadyRef.current) return;
    pendingExploredRef.current.push(...newExplored);
    if (exploredDebounceRef.current) {
      clearTimeout(exploredDebounceRef.current);
    }
    exploredDebounceRef.current = setTimeout(flushExploredUpdate, EXPLORED_DEBOUNCE_MS);
  }

  // --- Worker pre-computation ---
  // Asynchronously pre-computes visibility polygons via Web Worker.
  // Results are stored in visCacheRef so the sync render path hits cache.
  // Produces a 1-frame delay on first move, then cache keeps up.

  function scheduleWorkerPrecompute() {
    const bridge = workerBridgeRef.current;
    if (!bridge || precomputeScheduledRef.current) return;
    precomputeScheduledRef.current = true;

    // Collect inputs from current refs (snapshot)
    const currentVersion = wallsVersion.current;
    const blockingWalls = walls.current.filter(isWallBlocking);
    const gi = globalIllumination?.current ?? { level: 'bright' as const };
    const gSize = pageRef?.current?.gridSize ?? 64;
    const gsFt = gridSizeFt?.current ?? 5;
    const currentLitZones = litZoneCacheRef.current;

    const origins: Point[] = [];
    const ranges: number[] = [];

    for (const token of tokens.current) {
      if (!token.vision?.enabled) continue;
      if (!isGM && !controlledTokenIds.current.has(token.id)) continue;
      if (isVisionDisabledByCondition(token.conditions)) continue;
      if (origins.length >= LIMITS.MAX_VISION_TOKENS) break;

      const ev = computeEffectiveVision(token, gi, currentLitZones, gSize, gsFt);
      if (ev.disabled) continue;

      origins.push({ x: token.x, y: token.y });
      ranges.push(ev.effectiveRangePx);
    }

    if (origins.length === 0) {
      precomputeScheduledRef.current = false;
      return;
    }

    bridge.computeBatch(origins, blockingWalls, ranges).then(polygons => {
      precomputeScheduledRef.current = false;
      // Store in cache — sync render path will find them
      for (let i = 0; i < polygons.length; i++) {
        visCacheRef.current.set(origins[i], currentVersion, ranges[i], polygons[i]);
      }
      // Trigger re-render so the cached results get drawn
      engine.markDirty('fog');
    }).catch(() => {
      precomputeScheduledRef.current = false;
    });
  }

  // --- Visibility computation ---

  function computeAllVisibilityPolygons(cam: Camera, cssW: number, cssH: number): Point[][] {
    const totalStart = import.meta.env.DEV ? performance.now() : 0;
    const currentVersion = wallsVersion.current;

    // Rebuild spatial index if walls changed
    if (currentVersion !== spatialIndexVersionRef.current) {
      const blockingWalls = walls.current.filter(isWallBlocking);
      spatialIndexRef.current.build(blockingWalls);
      spatialIndexVersionRef.current = currentVersion;
    }

    // Invalidate vis cache if walls changed
    if (currentVersion !== prevWallsVersionRef.current) {
      visCacheRef.current.clear();
      prevWallsVersionRef.current = currentVersion;
    }

    // Compute viewport for culling
    const vp = computeViewportWorld(cam, cssW, cssH, VIEWPORT_MARGIN);

    const polygons: Point[][] = [];
    const allTokens = tokens.current;
    const gi = globalIllumination?.current ?? { level: 'bright' as const };
    const currentLitZones = getLitZones();
    const gSize = pageRef?.current?.gridSize ?? 64;
    const gsFt = gridSizeFt?.current ?? 5;

    let visionTokenCount = 0;
    let cacheHits = 0;
    let cacheMisses = 0;
    const perTokenMs: number[] = [];

    for (const token of allTokens) {
      // Only tokens with vision enabled and in controlled set (or GM sees all)
      if (!token.vision?.enabled) continue;
      if (!isGM && !controlledTokenIds.current.has(token.id)) continue;

      // Skip tokens with vision-disabling conditions (Blinded, Unconscious)
      if (isVisionDisabledByCondition(token.conditions)) continue;

      // Enforce MAX_VISION_TOKENS limit
      if (visionTokenCount >= LIMITS.MAX_VISION_TOKENS) {
        if (visionTokenCount === LIMITS.MAX_VISION_TOKENS) {
          console.warn(
            `[dynamic-fog] ${allTokens.filter(t => t.vision?.enabled).length} vision tokens exceed MAX_VISION_TOKENS (${LIMITS.MAX_VISION_TOKENS}). Truncating.`,
          );
        }
        break;
      }

      // Compute effective vision considering darkvision, lighting, conditions
      const effectiveVision = computeEffectiveVision(token, gi, currentLitZones, gSize, gsFt);
      if (effectiveVision.disabled) continue;

      visionTokenCount++;

      const origin: Point = { x: token.x, y: token.y };
      const maxRange = effectiveVision.effectiveRangePx;

      // Check cache
      const cached = visCacheRef.current.get(origin, currentVersion, maxRange);
      if (cached) {
        cacheHits++;
        polygons.push(cached);
        continue;
      }

      cacheMisses++;
      const tokenStart = import.meta.env.DEV ? performance.now() : 0;

      // Query ALL walls within vision range (not just viewport).
      // Walls outside the viewport can still block rays that pass through it.
      const culledWalls = maxRange > 0
        ? spatialIndexRef.current.query(origin.x, origin.y, maxRange)
        : spatialIndexRef.current.query(
            origin.x, origin.y,
            Math.max(
              Math.abs(vp.worldMaxX - origin.x),
              Math.abs(origin.x - vp.worldMinX),
              Math.abs(vp.worldMaxY - origin.y),
              Math.abs(origin.y - vp.worldMinY),
            ),
          );

      const polygon = computeVisibilityPolygon(origin, culledWalls, maxRange);
      visCacheRef.current.set(origin, currentVersion, maxRange, polygon);
      polygons.push(polygon);

      if (import.meta.env.DEV) {
        perTokenMs.push(performance.now() - tokenStart);
      }
    }

    // Update perf metrics in dev mode
    if (import.meta.env.DEV) {
      const totalMs = performance.now() - totalStart;
      const perf = perfRef.current;
      perf.perTokenMs = perTokenMs;
      perf.totalMs = totalMs;
      perf.cacheHits = cacheHits;
      perf.cacheMisses = cacheMisses;
      perf.wallCount = spatialIndexRef.current.size;
      perf.tokenCount = visionTokenCount;
      perf.frameCount++;

      // Console reporter: log every 60 frames if opt-in
      if (window.__DYNAMIC_FOG_PERF_LOG && perf.frameCount % 60 === 0) {
        const avgMs = perTokenMs.length > 0
          ? (perTokenMs.reduce((a, b) => a + b, 0) / perTokenMs.length).toFixed(1)
          : '0.0';
        const cacheRate = (cacheHits + cacheMisses) > 0
          ? ((cacheHits / (cacheHits + cacheMisses)) * 100).toFixed(0)
          : '0';
        console.warn(
          `[dynamic-fog perf] tokens=${visionTokenCount} walls=${spatialIndexRef.current.size} total=${totalMs.toFixed(1)}ms avg=${avgMs}ms/token cache=${cacheRate}%`
        );
      }
    }

    return polygons;
  }

  // --- Lighting computation ---

  function getLitZones(): LitZone[] | null {
    const gi = globalIllumination?.current;
    if (!gi || gi.level === 'bright') return null; // bright = no-op, no lit zones needed

    const page = pageRef?.current;
    if (!page) return null;

    const currentWallsVersion = wallsVersion.current;

    // Check cache validity
    if (
      litZoneCacheRef.current &&
      !lightingDirty.current &&
      litZoneWallsVersionRef.current === currentWallsVersion
    ) {
      return litZoneCacheRef.current;
    }

    const gridSize = page.gridSize || 64;
    const gsFt = gridSizeFt?.current ?? 5;

    // Build a live page snapshot using current refs instead of stale pageRef
    // pageRef.current.mapObjects and .tokens can be stale after in-session changes
    const livePage: PageState = {
      ...page,
      mapObjects: mapObjectsRef?.current ?? page.mapObjects,
      tokens: tokens.current.map(t => t as any) ?? page.tokens,
      globalIllumination: gi,
    };

    const sources = collectLightSources(livePage, gridSize, gsFt);
    const zones = computeLitZones(sources, walls.current, gridSize, gsFt);

    litZoneCacheRef.current = zones;
    litZoneWallsVersionRef.current = currentWallsVersion;
    lightingDirty.current = false;

    return zones;
  }

  // --- Render helpers ---

  function renderPolygonOnCanvas(
    ctx: CanvasRenderingContext2D,
    polygon: Point[],
    cam: Camera,
    dpr: number,
  ): void {
    if (polygon.length < 3) return;
    ctx.beginPath();
    const first = worldToScreen(polygon[0].x, polygon[0].y, cam);
    ctx.moveTo(first.x, first.y);
    for (let i = 1; i < polygon.length; i++) {
      const p = worldToScreen(polygon[i].x, polygon[i].y, cam);
      ctx.lineTo(p.x, p.y);
    }
    ctx.closePath();
    ctx.fill();
  }

  function renderFogRevealOnCanvas(
    ctx: CanvasRenderingContext2D,
    reveal: FogReveal,
    cam: Camera,
  ): void {
    if (reveal.type === 'polygon') {
      if (reveal.points.length < 3) return;
      ctx.beginPath();
      const first = worldToScreen(reveal.points[0].x, reveal.points[0].y, cam);
      ctx.moveTo(first.x, first.y);
      for (let i = 1; i < reveal.points.length; i++) {
        const p = worldToScreen(reveal.points[i].x, reveal.points[i].y, cam);
        ctx.lineTo(p.x, p.y);
      }
      ctx.closePath();
      ctx.fill();
    } else if (reveal.type === 'circle') {
      const sp = worldToScreen(reveal.x, reveal.y, cam);
      const sr = reveal.radius * cam.zoom;
      ctx.beginPath();
      ctx.arc(sp.x, sp.y, sr, 0, Math.PI * 2);
      ctx.fill();
    } else if (reveal.type === 'rect') {
      const sp = worldToScreen(reveal.x, reveal.y, cam);
      ctx.fillRect(sp.x, sp.y, reveal.width * cam.zoom, reveal.height * cam.zoom);
    }
  }

  // --- Main render pipeline ---

  const renderDynamicFog = useCallback(() => {
    const fogCanvas = engine.refs.current.fog;
    if (!fogCanvas) return;
    const fogCtx = fogCanvas.getContext('2d')!;
    const dpr = window.devicePixelRatio || 1;
    const cssW = fogCanvas.width / dpr;
    const cssH = fogCanvas.height / dpr;
    fogCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    fogCtx.clearRect(0, 0, cssW, cssH);

    const cam = engine.cameraRef.current;
    const w = fogCanvas.width;
    const h = fogCanvas.height;

    // Detect camera movement for multi-resolution rendering
    const lastCam = lastCamRef.current;
    const cameraChanged = !lastCam ||
      lastCam.offsetX !== cam.offsetX ||
      lastCam.offsetY !== cam.offsetY ||
      lastCam.zoom !== cam.zoom;

    if (cameraChanged) {
      // Offscreens are in screen-space — stale after any camera change
      visibilityDirty.current = true;
      exploredDirty.current = true;
      compositeDirty.current = true;
      lightColorDirty.current = true;
      lastCamRef.current = { offsetX: cam.offsetX, offsetY: cam.offsetY, zoom: cam.zoom };
      isMovingRef.current = true;

      // Schedule idle re-render at full resolution
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
      idleTimerRef.current = setTimeout(() => {
        isMovingRef.current = false;
        // Force full-res re-render
        visibilityDirty.current = true;
        exploredDirty.current = true;
        compositeDirty.current = true;
        lightColorDirty.current = true;
        engine.markDirty('fog');
      }, IDLE_RERENDER_MS);
    }

    // Multi-resolution: use reduced size during panning
    const scale = isMovingRef.current ? MOVING_SCALE : 1;
    const offW = Math.round(w * scale);
    const offH = Math.round(h * scale);

    // Ensure offscreen canvases (possibly at reduced resolution)
    const exploredOS = ensureOffscreen(exploredCanvasRef, offW, offH);
    const visibilityOS = ensureOffscreen(visibilityCanvasRef, offW, offH);
    const compositeOS = ensureOffscreen(compositeCanvasRef, offW, offH);

    // Adjusted DPR and CSS dimensions for reduced resolution
    const effDpr = dpr * scale;
    const effCssW = offW / effDpr;
    const effCssH = offH / effDpr;

    // Compute visibility polygons
    const visPolygons = computeAllVisibilityPolygons(cam, cssW, cssH);

    // Get lit zones (null in bright mode = no-op)
    const litZones = getLitZones();

    // DEBUG: trace dynamic fog pipeline (throttled to 1 per 60 frames)
    const gi = globalIllumination?.current ?? { level: 'bright' as const };
    if (perfRef.current.frameCount < 3) {
      console.warn('[DF]', {
        gi: gi.level,
        visPolys: visPolygons.length,
        controlledIds: [...controlledTokenIds.current],
        fogTokens: tokens.current.map(t => ({ id: t.id.slice(0,8), name: t.name, sheetId: t.characterSheetId?.slice(0,8), vision: !!t.vision?.enabled })),
      });
    }
    if (perfRef.current.frameCount % 60 === 0) {
      const visionTokens = tokens.current.filter(t => t.vision?.enabled);
      console.warn('[dynamic-fog] render:', {
        tokens: tokens.current.length,
        vision: visionTokens.length,
        visPolys: visPolygons.length,
        litZones: litZones?.length ?? 'null(bright)',
        gi: gi.level,
        explored: exploredRef.current.length,
        mapObjLights: (mapObjectsRef?.current ?? []).filter(o => o.category === 'light' && o.properties.state !== 'extinguished').length,
        isGM,
        walls: walls.current.length,
      });
    }

    // Edge case: no tokens with vision.enabled → full fog for player, GM dim overlay
    if (visPolygons.length === 0 && !isGM) {
      // Player: full black fog (nothing visible)
      fogCtx.fillStyle = 'rgba(0, 0, 0, 1)';
      fogCtx.fillRect(0, 0, cssW, cssH);

      // Still render explored areas as dim — reuse exploredOS for O(1) blit
      if (exploredRef.current.length > 0 || manualRevealed.current.length > 0) {
        if (exploredDirty.current || !exploredOS.valid) {
          const ec = exploredOS.ctx;
          ec.setTransform(effDpr, 0, 0, effDpr, 0, 0);
          ec.clearRect(0, 0, effCssW, effCssH);
          ec.fillStyle = 'white';
          for (const reveal of exploredRef.current) {
            renderFogRevealOnCanvas(ec, reveal, cam);
          }
          for (const reveal of manualRevealed.current) {
            renderFogRevealOnCanvas(ec, reveal, cam);
          }
          exploredOS.valid = true;
          exploredDirty.current = false;
        }
        fogCtx.globalCompositeOperation = 'destination-out';
        fogCtx.globalAlpha = 0.5;
        fogCtx.save();
        fogCtx.setTransform(1, 0, 0, 1, 0, 0);
        fogCtx.drawImage(exploredOS.canvas, 0, 0, offW, offH, 0, 0, w, h);
        fogCtx.restore();
        fogCtx.globalCompositeOperation = 'source-over';
        fogCtx.globalAlpha = 1.0;
      }
      // Clear stale visibility polygons so token rendering doesn't use old data
      lastVisibilityPolygonsRef.current = visPolygons;
      return;
    }

    if (visPolygons.length === 0 && isGM) {
      // GM: dim overlay with explored areas cutout
      fogCtx.fillStyle = 'rgba(0, 0, 0, 0.4)';
      fogCtx.fillRect(0, 0, cssW, cssH);
      if (exploredRef.current.length > 0 || manualRevealed.current.length > 0) {
        fogCtx.globalCompositeOperation = 'destination-out';
        fogCtx.globalAlpha = 0.4;
        for (const reveal of exploredRef.current) {
          renderFogRevealOnCanvas(fogCtx, reveal, cam);
        }
        for (const reveal of manualRevealed.current) {
          renderFogRevealOnCanvas(fogCtx, reveal, cam);
        }
        fogCtx.globalCompositeOperation = 'source-over';
        fogCtx.globalAlpha = 1.0;
      }
      return;
    }

    // --- Explored diff tracking (any tab with active visibility) ---
    if (visPolygons.length > 0) {
      const prevCount = exploredRef.current.length;
      // Union all visibility polygons and check for new explored areas
      for (const polygon of visPolygons) {
        const newExplored = computeExploredDiff(
          polygon,
          exploredRef.current,
          EXPLORED_DIFF_THRESHOLD,
        );
        if (newExplored.length > 0) {
          exploredRef.current.push(...newExplored);
          exploredDirty.current = true;
          scheduleExploredUpdate(newExplored);
        }
      }
      if (exploredRef.current.length !== prevCount) {
        console.warn('[explored] new areas:', exploredRef.current.length - prevCount, 'total:', exploredRef.current.length);
      }
    }
    lastVisibilityPolygonsRef.current = visPolygons;

    // --- Rebuild explored canvas if dirty ---
    if (exploredDirty.current || !exploredOS.valid) {
      const ec = exploredOS.ctx;
      ec.setTransform(effDpr, 0, 0, effDpr, 0, 0);
      ec.clearRect(0, 0, effCssW, effCssH);
      // White cutouts on transparent background (to be used with destination-out)
      ec.fillStyle = 'white';
      // Draw explored areas
      for (const reveal of exploredRef.current) {
        renderFogRevealOnCanvas(ec, reveal, cam);
      }
      // Hybrid mode: manual reveals also treated as explored
      for (const reveal of manualRevealed.current) {
        renderFogRevealOnCanvas(ec, reveal, cam);
      }
      exploredOS.valid = true;
      exploredDirty.current = false;
      compositeDirty.current = true;
    }

    // --- Rebuild visibility canvas if dirty ---
    if (visibilityDirty.current || !visibilityOS.valid) {
      const vc = visibilityOS.ctx;
      vc.setTransform(effDpr, 0, 0, effDpr, 0, 0);
      vc.clearRect(0, 0, effCssW, effCssH);
      vc.fillStyle = 'white';
      // Draw union of all visibility polygons
      for (const polygon of visPolygons) {
        renderPolygonOnCanvas(vc, polygon, cam, effDpr);
      }
      visibilityOS.valid = true;
      visibilityDirty.current = false;
      compositeDirty.current = true;
    }

    // --- Composite canvas ---
    const cc = compositeOS.ctx;
    cc.setTransform(effDpr, 0, 0, effDpr, 0, 0);
    cc.clearRect(0, 0, effCssW, effCssH);

    if (isGM) {
      // GM variation: overlay intensity depends on illumination level
      const giLevel = (globalIllumination?.current?.level) ?? 'bright';
      const overlayAlpha = giLevel === 'dark' ? 0.75 : giLevel === 'dim' ? 0.55 : 0.4;
      // Visibility cutout: in dim/dark, don't fully clear — leave residual darkness
      const visCutoutAlpha = giLevel === 'dark' ? 0.5 : giLevel === 'dim' ? 0.7 : 1.0;

      cc.fillStyle = `rgba(0, 0, 0, ${overlayAlpha})`;
      cc.fillRect(0, 0, effCssW, effCssH);

      // Cut out explored areas at partial opacity
      cc.globalCompositeOperation = 'destination-out';
      cc.globalAlpha = 0.4;
      cc.save();
      cc.setTransform(1, 0, 0, 1, 0, 0);
      cc.drawImage(exploredOS.canvas, 0, 0);
      cc.restore();

      if (giLevel === 'dark' && litZones) {
        // Dark mode: token vision only works within lit zones or darkvision range
        // Don't draw flat visibility cutout — clip it to lit zones instead

        // First: show lit zones (unclipped) so GM sees where light is
        cc.globalAlpha = 0.25;
        for (const zone of litZones) {
          if (zone.dimPolygon.length >= 3) {
            renderPolygonOnCanvas(cc, zone.dimPolygon, cam, effDpr);
          }
        }
        cc.globalAlpha = 0.4;
        for (const zone of litZones) {
          if (zone.brightPolygon.length >= 3) {
            renderPolygonOnCanvas(cc, zone.brightPolygon, cam, effDpr);
          }
        }

        // Then: show "what tokens actually see" = visibility ∩ (lit zones ∪ darkvision)
        // Build a clip from lit zone polygons + darkvision polygons
        const gSize = pageRef?.current?.gridSize ?? 64;
        const gsFtVal = gridSizeFt?.current ?? 5;
        cc.save();
        cc.beginPath();
        // Add lit zone dim polygons (dim includes bright area)
        for (const zone of litZones) {
          if (zone.dimPolygon.length < 3) continue;
          const first = worldToScreen(zone.dimPolygon[0].x, zone.dimPolygon[0].y, cam);
          cc.moveTo(first.x, first.y);
          for (let i = 1; i < zone.dimPolygon.length; i++) {
            const p = worldToScreen(zone.dimPolygon[i].x, zone.dimPolygon[i].y, cam);
            cc.lineTo(p.x, p.y);
          }
          cc.closePath();
        }
        // Add darkvision polygons
        for (const token of tokens.current) {
          if (!token.vision?.enabled || !token.vision.darkvision) continue;
          if (isVisionDisabledByCondition(token.conditions)) continue;
          const giVal = globalIllumination?.current ?? { level: 'bright' as const };
          const ev = computeEffectiveVision(token, giVal, litZones, gSize, gsFtVal);
          if (ev.disabled) continue;
          const dvRange = Math.max(ev.brightRangePx, ev.dimRangePx);
          if (dvRange <= 0) continue;
          const origin: Point = { x: token.x, y: token.y };
          const dvPoly = computeVisibilityPolygon(origin, walls.current.filter(isWallBlocking), dvRange);
          if (dvPoly.length < 3) continue;
          const first = worldToScreen(dvPoly[0].x, dvPoly[0].y, cam);
          cc.moveTo(first.x, first.y);
          for (let i = 1; i < dvPoly.length; i++) {
            const p = worldToScreen(dvPoly[i].x, dvPoly[i].y, cam);
            cc.lineTo(p.x, p.y);
          }
          cc.closePath();
        }
        cc.clip();

        // Within lit+darkvision clip: draw visibility cutout
        cc.globalAlpha = visCutoutAlpha;
        cc.save();
        cc.setTransform(1, 0, 0, 1, 0, 0);
        cc.drawImage(visibilityOS.canvas, 0, 0);
        cc.restore();

        cc.restore(); // Remove clip

        // Darkvision extra cutout (unclipped — darkvision is its own bounded polygon)
        for (const token of tokens.current) {
          if (!token.vision?.enabled || !token.vision.darkvision) continue;
          if (isVisionDisabledByCondition(token.conditions)) continue;
          const giVal = globalIllumination?.current ?? { level: 'bright' as const };
          const ev = computeEffectiveVision(token, giVal, litZones, gSize, gsFtVal);
          if (ev.disabled || ev.brightRangePx <= 0) continue;
          cc.globalAlpha = 0.3;
          const dvOrigin: Point = { x: token.x, y: token.y };
          const dvPoly = computeVisibilityPolygon(dvOrigin, walls.current.filter(isWallBlocking), ev.brightRangePx);
          renderPolygonOnCanvas(cc, dvPoly, cam, effDpr);
        }
      } else {
        // Bright/dim mode: flat visibility cutout works correctly
        cc.globalAlpha = visCutoutAlpha;
        cc.save();
        cc.setTransform(1, 0, 0, 1, 0, 0);
        cc.drawImage(visibilityOS.canvas, 0, 0);
        cc.restore();

        // Cut out lit zones so GM can see illuminated areas (dim mode)
        if (litZones && giLevel !== 'bright') {
          cc.globalAlpha = 0.25;
          for (const zone of litZones) {
            if (zone.dimPolygon.length >= 3) {
              renderPolygonOnCanvas(cc, zone.dimPolygon, cam, effDpr);
            }
          }
          cc.globalAlpha = 0.5;
          for (const zone of litZones) {
            if (zone.brightPolygon.length >= 3) {
              renderPolygonOnCanvas(cc, zone.brightPolygon, cam, effDpr);
            }
          }
        }

        // In dim with darkvision tokens: extra cutout for darkvision zones
        if (giLevel !== 'bright' && litZones !== null) {
          const gSize = pageRef?.current?.gridSize ?? 64;
          const gsFtVal = gridSizeFt?.current ?? 5;
          for (const token of tokens.current) {
            if (!token.vision?.enabled || !token.vision.darkvision) continue;
            if (isVisionDisabledByCondition(token.conditions)) continue;
            const giVal = globalIllumination?.current ?? { level: 'bright' as const };
            const ev = computeEffectiveVision(token, giVal, litZones, gSize, gsFtVal);
            if (ev.disabled || ev.brightRangePx <= 0) continue;
            cc.globalAlpha = 0.3;
            const dvOrigin: Point = { x: token.x, y: token.y };
            const dvPoly = computeVisibilityPolygon(dvOrigin, walls.current.filter(isWallBlocking), ev.brightRangePx);
            renderPolygonOnCanvas(cc, dvPoly, cam, effDpr);
          }
        }
      }

      cc.globalCompositeOperation = 'source-over';
      cc.globalAlpha = 1.0;
    } else if (!litZones) {
      // Player variation — bright mode (no lighting): full fog with visibility cutouts
      cc.fillStyle = 'rgba(0, 0, 0, 1)';
      cc.fillRect(0, 0, effCssW, effCssH);

      // Cut out explored areas at 50% opacity (dim fog)
      cc.globalCompositeOperation = 'destination-out';
      cc.globalAlpha = 0.5;
      cc.save();
      cc.setTransform(1, 0, 0, 1, 0, 0);
      cc.drawImage(exploredOS.canvas, 0, 0);
      cc.restore();

      // Cut out visible areas at 100% opacity (clear)
      cc.globalAlpha = 1.0;
      cc.save();
      cc.setTransform(1, 0, 0, 1, 0, 0);
      cc.drawImage(visibilityOS.canvas, 0, 0);
      cc.restore();

      cc.globalCompositeOperation = 'source-over';
      cc.globalAlpha = 1.0;
    } else {
      // Player variation — dim/dark mode: fog with lighting zones
      // Start with full fog
      cc.fillStyle = 'rgba(0, 0, 0, 1)';
      cc.fillRect(0, 0, effCssW, effCssH);

      // Cut out explored areas at 50% opacity (dim fog)
      cc.globalCompositeOperation = 'destination-out';
      cc.globalAlpha = 0.5;
      cc.save();
      cc.setTransform(1, 0, 0, 1, 0, 0);
      cc.drawImage(exploredOS.canvas, 0, 0);
      cc.restore();

      // Clip all lighting cutouts to player's visibility polygon
      // Without this, lit zones on the far side of the map would punch through fog
      cc.save();
      cc.beginPath();
      for (const polygon of visPolygons) {
        if (polygon.length < 3) continue;
        const first = worldToScreen(polygon[0].x, polygon[0].y, cam);
        cc.moveTo(first.x, first.y);
        for (let i = 1; i < polygon.length; i++) {
          const p = worldToScreen(polygon[i].x, polygon[i].y, cam);
          cc.lineTo(p.x, p.y);
        }
        cc.closePath();
      }
      cc.clip();

      // 1. Dim zones (clipped to visibility): cut at 50% opacity
      cc.globalAlpha = 0.5;
      for (const zone of litZones) {
        if (zone.dimPolygon.length >= 3) {
          renderPolygonOnCanvas(cc, zone.dimPolygon, cam, effDpr);
        }
      }

      // Darkvision: tokens with darkvision get pseudo-dim zone in dark, or upgrade in dim
      const gi = globalIllumination?.current;
      const gSize = pageRef?.current?.gridSize ?? 64;
      const gsFtVal = gridSizeFt?.current ?? 5;
      if (gi && gi.level !== 'bright') {
        for (const token of tokens.current) {
          if (!token.vision?.enabled || !token.vision.darkvision) continue;
          if (!isGM && !controlledTokenIds.current.has(token.id)) continue;
          if (isVisionDisabledByCondition(token.conditions)) continue;
          const ev = computeEffectiveVision(token, gi, litZones, gSize, gsFtVal);
          if (ev.disabled || ev.dimRangePx <= 0) continue;
          const origin: Point = { x: token.x, y: token.y };
          const dvPolygon = computeVisibilityPolygon(origin, walls.current.filter(isWallBlocking), ev.dimRangePx);
          renderPolygonOnCanvas(cc, dvPolygon, cam, effDpr);
        }
      }

      // 2. Bright zones (clipped to visibility): cut at 100% opacity
      cc.globalAlpha = 1.0;
      for (const zone of litZones) {
        if (zone.brightPolygon.length >= 3) {
          renderPolygonOnCanvas(cc, zone.brightPolygon, cam, effDpr);
        }
      }

      // Darkvision in dim global upgrades dim to bright (additional 50% cutout)
      if (gi && gi.level === 'dim') {
        for (const token of tokens.current) {
          if (!token.vision?.enabled || !token.vision.darkvision) continue;
          if (!isGM && !controlledTokenIds.current.has(token.id)) continue;
          if (isVisionDisabledByCondition(token.conditions)) continue;
          const ev = computeEffectiveVision(token, gi, litZones, gSize, gsFtVal);
          if (ev.disabled || ev.brightRangePx <= 0) continue;
          const origin: Point = { x: token.x, y: token.y };
          const dvPolygon = computeVisibilityPolygon(origin, walls.current.filter(isWallBlocking), ev.brightRangePx);
          renderPolygonOnCanvas(cc, dvPolygon, cam, effDpr);
        }
      }

      cc.restore(); // Remove visibility clip

      cc.globalCompositeOperation = 'source-over';
      cc.globalAlpha = 1.0;
    }

    compositeOS.valid = true;
    compositeDirty.current = false;

    // Blit composite to fog canvas (scale up if reduced resolution)
    fogCtx.save();
    fogCtx.setTransform(1, 0, 0, 1, 0, 0);
    if (scale < 1) {
      // Scale up from reduced resolution
      fogCtx.drawImage(compositeOS.canvas, 0, 0, offW, offH, 0, 0, w, h);
    } else {
      fogCtx.drawImage(compositeOS.canvas, 0, 0);
    }
    fogCtx.restore();

    // --- Light color overlay (non-bright mode) ---
    if (litZones) {
      const page = pageRef?.current;
      if (page) {
        const gSize = page.gridSize || 64;
        const gsFtVal = gridSizeFt?.current ?? 5;
        const gi = globalIllumination?.current;
        const giColor = gi?.color;
        const giLevel = gi?.level ?? 'bright';

        const sources = collectLightSources(page, gSize, gsFtVal);
        const coloredSources = collectColoredSources(sources);

        // Only render if there are colored sources or global color tint
        if (coloredSources.length > 0 || giColor) {
          const lightColorOS = ensureOffscreen(lightColorCanvasRef, w, h);

          if (lightColorDirty.current || !lightColorOS.valid) {
            renderLightColorOverlay(
              lightColorOS.ctx,
              coloredSources,
              giColor,
              giLevel,
              cam,
              dpr,
              cssW,
              cssH,
            );
            lightColorOS.valid = true;
            lightColorDirty.current = false;
          }

          // Blit light color overlay onto fog canvas
          // source-atop: draws only where fog canvas already has pixels (the fog area)
          fogCtx.save();
          fogCtx.globalCompositeOperation = 'source-atop';
          fogCtx.setTransform(1, 0, 0, 1, 0, 0);
          fogCtx.drawImage(lightColorOS.canvas, 0, 0);
          fogCtx.restore();
        }
      }
    }
  }, [engine, isGM, controlledTokenIds, walls, wallsVersion, tokens, manualRevealed, sendExploredUpdate, globalIllumination, pageRef, gridSizeFt, mapObjectsRef]);

  // Flush pending explored on page unload to prevent data loss
  useEffect(() => {
    const handler = () => flushExploredUpdate();
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    renderDynamicFog,
    invalidateVisibility,
    invalidateExplored,
    invalidateAll,
    invalidateLighting,
    setExplored,
    appendExplored,
    exploredRef,
    visibilityPolygonsRef: lastVisibilityPolygonsRef,
    litZoneCacheRef,
  };
}
